/**
 * Theme System for TCF Banner
 * Supports multiple visual themes with auto-detection
 */

// Theme definitions
export const themes = {
  // Default Captain Compliance theme (clean, professional)
  default: {
    name: 'default',
    variables: {
      '--tcf-primary': '#3498db',
      '--tcf-primary-hover': '#2980b9',
      '--tcf-text-dark': '#1a1a2e',
      '--tcf-text-light': '#ffffff',
      '--tcf-text-muted': '#666666',
      '--tcf-bg-banner': '#ffffff',
      '--tcf-bg-modal': '#ffffff',
      '--tcf-bg-overlay': 'rgba(0, 0, 0, 0.5)',
      '--tcf-border': '#e0e0e0',
      '--tcf-shadow': '0 -2px 10px rgba(0, 0, 0, 0.1)',
      '--tcf-radius': '4px',
      '--tcf-accent': '#c0392b',
    }
  },

  // CookieBikes theme (dark, sporty, modern)
  cookiebikes: {
    name: 'cookiebikes',
    variables: {
      '--tcf-primary': '#f97316',        // Orange accent (sporty)
      '--tcf-primary-hover': '#ea580c',
      '--tcf-text-dark': '#ffffff',       // Light text on dark
      '--tcf-text-light': '#ffffff',
      '--tcf-text-muted': '#a1a1aa',
      '--tcf-bg-banner': '#18181b',       // Dark zinc background
      '--tcf-bg-modal': '#27272a',
      '--tcf-bg-overlay': 'rgba(0, 0, 0, 0.7)',
      '--tcf-border': '#3f3f46',
      '--tcf-shadow': '0 -4px 20px rgba(0, 0, 0, 0.3)',
      '--tcf-radius': '8px',
      '--tcf-accent': '#f97316',
    }
  },

  // Dark theme (generic dark mode)
  dark: {
    name: 'dark',
    variables: {
      '--tcf-primary': '#60a5fa',
      '--tcf-primary-hover': '#3b82f6',
      '--tcf-text-dark': '#f4f4f5',
      '--tcf-text-light': '#ffffff',
      '--tcf-text-muted': '#a1a1aa',
      '--tcf-bg-banner': '#1f2937',
      '--tcf-bg-modal': '#374151',
      '--tcf-bg-overlay': 'rgba(0, 0, 0, 0.7)',
      '--tcf-border': '#4b5563',
      '--tcf-shadow': '0 -4px 20px rgba(0, 0, 0, 0.4)',
      '--tcf-radius': '8px',
      '--tcf-accent': '#10b981',
    }
  }
};

// Domain to theme mapping
const domainThemes = {
  'cookiebikes.com': 'cookiebikes',
  'www.cookiebikes.com': 'cookiebikes',
  // Add more domain mappings here
};

/**
 * Detect theme from various sources
 * Priority: 1) URL param, 2) data attribute, 3) domain, 4) default
 */
export function detectTheme() {
  // 1. Check URL parameter (?theme=cookiebikes)
  const urlParams = new URLSearchParams(window.location.search);
  const urlTheme = urlParams.get('theme');
  if (urlTheme && themes[urlTheme]) {
    console.log(`TCF Banner: Using theme from URL param: ${urlTheme}`);
    return urlTheme;
  }

  // 2. Check data attribute on script tag
  const scriptTag = document.querySelector('script[data-tcf-theme]');
  if (scriptTag) {
    const dataTheme = scriptTag.getAttribute('data-tcf-theme');
    if (dataTheme && themes[dataTheme]) {
      console.log(`TCF Banner: Using theme from data attribute: ${dataTheme}`);
      return dataTheme;
    }
  }

  // 3. Check domain mapping
  const hostname = window.location.hostname;
  if (domainThemes[hostname]) {
    console.log(`TCF Banner: Using theme for domain ${hostname}: ${domainThemes[hostname]}`);
    return domainThemes[hostname];
  }

  // 4. Default theme
  return 'default';
}

/**
 * Apply theme variables to document
 */
export function applyTheme(themeName) {
  const theme = themes[themeName] || themes.default;
  const root = document.documentElement;

  // Apply CSS variables
  Object.entries(theme.variables).forEach(([property, value]) => {
    root.style.setProperty(property, value);
  });

  // Add theme class to body for additional styling hooks
  document.body.classList.remove('tcf-theme-default', 'tcf-theme-cookiebikes', 'tcf-theme-dark');
  document.body.classList.add(`tcf-theme-${theme.name}`);

  console.log(`TCF Banner: Applied theme: ${theme.name}`);
  return theme.name;
}

/**
 * Initialize theme system
 */
export function initTheme() {
  const themeName = detectTheme();
  return applyTheme(themeName);
}

/**
 * Get current theme
 */
export function getCurrentTheme() {
  return detectTheme();
}

/**
 * Set theme programmatically
 */
export function setTheme(themeName) {
  if (themes[themeName]) {
    return applyTheme(themeName);
  }
  console.warn(`TCF Banner: Unknown theme "${themeName}", using default`);
  return applyTheme('default');
}




